import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpContextToken,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { CookieService } from "ngx-cookie-service";
import { Observable } from "rxjs";
import { catchError, finalize, timeout } from "rxjs/operators";
import { environment } from "../../../environments/environment";
import { AccountService } from "../../customer-portal/services/account.service";
import { WindowRef } from "../common/global-refs";
import { LoaderService } from "./loader.service";
import { NotificationService } from "./notification.service";

const ERROR_MESSAGES = {
  INTERNET_CONNECTION: "Please check your internet connection and try again",
  DEFAULT: "Something went wrong",
};
// matIconRegistry requests should be made without interceptor
const ICON_URL = "assets/images/custom-icons";

// Optional: allow callers to opt out of base prefixing per-request
export const SKIP_BASE_URL = new HttpContextToken<boolean>(() => false);

/** Do not send Bearer token (e.g. SAS/presigned URLs where query string is the auth). */
export const SKIP_AUTH_HEADER = new HttpContextToken<boolean>(() => false);

/** Do not push the global loader overlay for this request. */
export const SKIP_LOADER = new HttpContextToken<boolean>(() => false);

/**
 * Do not show the interceptor's error toast or non-401 handling; caller handles errors.
 * 401 still runs session cleanup (logout / navigate).
 */
export const SKIP_GLOBAL_ERROR_HANDLER = new HttpContextToken<boolean>(() => false);

@Injectable()
export class RequestInterceptor implements HttpInterceptor {
  constructor(
    private readonly cookieService: CookieService,
    private readonly router: Router,
    private readonly notificationService: NotificationService,
    private readonly windowRef: WindowRef,
    private readonly loaderService: LoaderService,
    private readonly dialogRef: MatDialog,
    private readonly accountService: AccountService,
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (
      request.url !== environment.API_TOKEN_URL &&
      !request.url.includes(ICON_URL)
    ) {
      let headers = request.headers;
      const accessToken = this.cookieService.get("access_token");
      const requestType = request.params.get("requestType");
      const requestResponseType = request.responseType;
      const method = request.method.toUpperCase();

      // Only set Content-Type for requests with a body (POST, PUT, PATCH, etc.)
      // GET and DELETE requests should not have Content-Type header
      if (requestType !== "upload") {
        if (method !== "GET" && method !== "DELETE" && method !== "HEAD") {
          headers = headers.set("Content-Type", "application/json");
        } else {
          // Explicitly remove Content-Type for GET/DELETE/HEAD requests
          headers = headers.delete("Content-Type");
        }
      }

      // Only add Authorization when the target expects it. Azure Blob SAS URLs (and similar)
      // authenticate via the URL only; a Bearer header causes 400 "Authorization header" errors.
      if (accessToken && !this.shouldOmitBearerAuth(request)) {
        headers = headers.set("Authorization", `Bearer ${accessToken}`);
      }

      let URL = environment.API_URL;

      if (requestType === "paymentsFunction") {
        URL = environment.PAYMENTS_FUNCTION_API_URL;
      }

      // Only prefix relative URLs; leave absolute URLs intact
      const isAbsolute = /^https?:\/\//i.test(request.url);
      const skipBase = request.context.get(SKIP_BASE_URL) === true;
      const shouldPrefix = !isAbsolute && !skipBase;

      // Normalize slashes
      const base = (URL || "").replace(/\/$/, "");
      const path = request.url.replace(/^\/+/, "");
      const finalUrl = shouldPrefix ? `${base}/${path}` : request.url;

      let params = request.params.delete("requestType");
      const previewEnvironment =
        this.windowRef.nativeWindow["previewEnvironment"];
      if (
        previewEnvironment != null &&
        /\.(golmn|groundsguyspro)\.com/i.test(finalUrl)
      ) {
        params = params.set("x-ms-routing-name", previewEnvironment);
      }

      request = request.clone({
        params,
        headers,
        responseType: requestResponseType || "json",
        url: finalUrl,
      });

      // send request without spinner and centralized error handling
      if (requestType === "silent") {
        return next.handle(request);
      }
    }

    const skipLoader = request.context.get(SKIP_LOADER) === true;
    const skipGlobalError =
      request.context.get(SKIP_GLOBAL_ERROR_HANDLER) === true;

    if (!skipLoader) {
      this.loaderService.pushRequest();
    }

    return next.handle(request).pipe(
      timeout(400000),
      catchError((res) => {
        if (skipGlobalError) {
          if (res instanceof HttpErrorResponse && res.status === 401) {
            this.handleError(res);
          }
        } else {
          this.handleError(res);
        }
        throw res;
      }),
      finalize(() => {
        if (!skipLoader) {
          this.loaderService.removeRequest();
        }
      }),
    );
  }

  private shouldOmitBearerAuth(request: HttpRequest<unknown>): boolean {
    if (request.context.get(SKIP_AUTH_HEADER) === true) {
      return true;
    }
    const url = request.url;
    if (!/^https?:\/\//i.test(url)) {
      return false;
    }
    try {
      const host = new URL(url).hostname.toLowerCase();
      return host.endsWith(".blob.core.windows.net");
    } catch {
      return false;
    }
  }

  private getError(res: HttpErrorResponse): string {
    if (!this.windowRef.nativeWindow.navigator.onLine) {
      return ERROR_MESSAGES.INTERNET_CONNECTION;
    }

    return (
      (res.error && (res.error.Message || res.error.error_description)) ||
      res.statusText ||
      ERROR_MESSAGES.DEFAULT
    );
  }

  private handleError(res: HttpErrorResponse): void {
    if (res.status === 401) {
      this.dialogRef.closeAll(); // close all opened dialogs on session expiration
      this.accountService.resetAccount();
      this.router.navigate(["/login"]);
    } else {
      const error = this.getError(res);
      this.notificationService.error(error);
    }
  }
}
